import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from './Notification';
import { theme } from '../theme';
import { UserBadge } from '../types';
import { logger } from '../utils/logger';
import { useResponsive } from '../hooks/useResponsive';

interface UserBadgesProps {
  badges: UserBadge[];
  isVisible?: boolean;
  onClose?: () => void;
}

export const UserBadges: React.FC<UserBadgesProps> = ({
  badges,
  isVisible = true,
  onClose
}) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { notification, showNotification, hideNotification } = useNotification();
  
  const [loading, setLoading] = useState(false);

  // If badges are provided directly, don't fetch them
  const shouldFetch = !badges || badges.length === 0;

  useEffect(() => {
    if (isVisible && shouldFetch && user) {
      fetchUserBadges();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, shouldFetch, user]);

  const fetchUserBadges = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/gamification/user/${user.id}/badges`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update the badges state if we're fetching
        if (shouldFetch) {
          // This would require adding a setBadges state, but since we're not using it in this mode,
          // we'll just handle the case where badges are provided directly
        }
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error cargando badges', 'error');
      }
    } catch (error) {
      logger.error('Error fetching user badges:', error);
      showNotification('Error de conexión', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getBadgeStyle = (badge: UserBadge) => ({
    background: `linear-gradient(135deg, ${badge.color}20, ${badge.color}40)`,
    border: `2px solid ${badge.color}`,
    boxShadow: `0 4px 15px ${badge.color}40`,
    position: 'relative' as const,
    overflow: 'hidden'
  });

  const getBadgeGlow = (badge: UserBadge) => ({
    position: 'absolute' as const,
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: `radial-gradient(circle, ${badge.color}30 0%, transparent 70%)`,
    animation: 'pulse 2s infinite',
    pointerEvents: 'none' as const
  });

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '16px' : '32px'
    }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.card,
        padding: isMobile ? '16px' : theme.spacing.lg,
        maxWidth: isMobile ? '100%' : '800px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: theme.colors.shadow,
        border: `2px solid ${theme.colors.primary}`,
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing.lg
        }}>
          <h2 style={{
            margin: 0,
            color: '#FFD700',
            fontFamily: theme.fonts.heading,
            fontSize: isMobile ? '20px' : theme.fonts.sizeTitle,
            textShadow: '0 0 10px #FFD700',
            background: 'linear-gradient(45deg, #FFD700, #FFA500)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            🏆 Mis Logros y Badges
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: theme.colors.textSecondary
            }}
          >
            ×
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: theme.spacing.lg }}>
            <div style={{
              fontSize: '24px',
              animation: 'spin 1s linear infinite'
            }}>
              ⚽
            </div>
            <p style={{ color: theme.colors.textSecondary }}>Cargando badges...</p>
          </div>
        ) : badges.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: theme.spacing.lg,
            color: theme.colors.textSecondary
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎯</div>
            <h3 style={{ color: theme.colors.text, marginBottom: '8px' }}>
              Aún no tienes badges
            </h3>
            <p style={{ color: theme.colors.textSecondary }}>
              ¡Deja reviews a tus compañeros para desbloquear logros increíbles!
            </p>
          </div>
        ) : (
          <div>
            <div style={{
              textAlign: 'center',
              marginBottom: theme.spacing.lg,
              padding: theme.spacing.md,
              background: 'linear-gradient(135deg, #FFD70020, #FFA50020)',
              borderRadius: theme.borderRadius.card,
              border: '1px solid #FFD70040'
            }}>
              <h3 style={{
                margin: '0 0 8px 0',
                color: '#FFD700',
                fontFamily: theme.fonts.body,
                fontSize: theme.fonts.sizeBody
              }}>
                Total de Badges: {badges.length}
              </h3>
              <p style={{
                margin: 0,
                color: theme.colors.textSecondary,
                fontSize: theme.fonts.sizeSmall
              }}>
                ¡Sigue dejando reviews para desbloquear más logros!
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: theme.spacing.md
            }}>
              {badges.map((badge) => (
                <div
                  key={badge.id}
                  style={{
                    padding: theme.spacing.md,
                    borderRadius: theme.borderRadius.card,
                    ...getBadgeStyle(badge),
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    transform: 'scale(1)',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <div style={getBadgeGlow(badge)} />
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.md,
                    marginBottom: theme.spacing.sm
                  }}>
                    <div style={{
                      fontSize: '32px',
                      filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.3))'
                    }}>
                      {badge.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h4 style={{
                        margin: '0 0 4px 0',
                        color: badge.color,
                        fontFamily: theme.fonts.body,
                        fontSize: theme.fonts.sizeBody,
                        fontWeight: theme.fonts.weightBold,
                        textShadow: `0 0 5px ${badge.color}`
                      }}>
                        {badge.name}
                      </h4>
                      <p style={{
                        margin: 0,
                        color: theme.colors.textSecondary,
                        fontSize: theme.fonts.sizeSmall,
                        lineHeight: '1.4'
                      }}>
                        {badge.description}
                      </p>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: theme.spacing.sm,
                    paddingTop: theme.spacing.sm,
                    borderTop: `1px solid ${badge.color}40`
                  }}>
                    <span style={{
                      color: badge.color,
                      fontSize: theme.fonts.sizeSmall,
                      fontWeight: theme.fonts.weightBold
                    }}>
                      {badge.requirement}
                    </span>
                    <span style={{
                      color: theme.colors.textSecondary,
                      fontSize: theme.fonts.sizeSmall
                    }}>
                      {new Date(badge.earnedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Botón cerrar */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: theme.spacing.lg
        }}>
          <button
            onClick={onClose}
            style={{
              padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
              backgroundColor: 'linear-gradient(135deg, #FFD700, #FFA500)',
              color: '#000',
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontFamily: theme.fonts.body,
              fontSize: theme.fonts.sizeBody,
              fontWeight: theme.fonts.weightBold,
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 215, 0, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 215, 0, 0.3)';
            }}
          >
            Cerrar
          </button>
        </div>

        {/* Notificación */}
        {notification.isVisible && (
          <button type="button" onClick={hideNotification} style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '16px 20px',
            borderRadius: '8px',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '14px',
            zIndex: 1001,
            maxWidth: '400px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            border: 'none',
            backgroundColor: notification.type === 'success' ? theme.colors.success :
                           notification.type === 'error' ? theme.colors.danger :
                           notification.type === 'warning' ? theme.colors.warning : theme.colors.primary
          }}>
            <span>{notification.type === 'success' ? '✅' : notification.type === 'error' ? '❌' : notification.type === 'warning' ? '⚠️' : 'ℹ️'}</span>
            <span>{notification.message}</span>
          </button>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}; 